import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlusCircle } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { ErrorToaster } from "../components/toaster/Toaster";
import { generatePdf } from "../../utils/helper";
import dayjs from "dayjs";
import { Table } from "antd";
import ReportServices from "../../services/Report";
import { all_routes } from "../../Router/all_routes";
import { useSelector } from "react-redux";

const ExchangeReceipt = () => {
  const invoiceRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => ({
    userData: state.user_data
  }));

  const storeLogo = userData?.store?.logoBase64 || '/assets/img/mattress-plus-logo.png';
  const storeQrCode = userData?.store?.qrCodeBase64 || '/assets/img/store/store-qr.png';

  // *For Loading
  const [loading, setLoading] = useState(false);

  // *For Exchange Receipt Data
  const [receiptData, setReceiptData] = useState(null);

  // *For Generate PDF
  const handleGeneratePdf = async () => {
    setLoading(true);
    try {
      await generatePdf([invoiceRef], 'exchange-receipt');
    } catch (error) {
      console.log('handleGeneratePdf => error:', error);
    } finally {
      setLoading(false);
    }
  };

  // *For Get Exchange Receipt Details
  const getExchangeReceiptDetails = async () => {
    try {
      setLoading(true);
      const response = await ReportServices.generateExchangeReceipt(id);
      setReceiptData(response.data || response);
    } catch (error) {
      console.log('Error fetching exchange receipt details:', error);
      ErrorToaster(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getExchangeReceiptDetails();
    }
  }, [id]);

  // --- Columns for original items ---
  const originalItemsColumns = [
    {
      title: "Item Name",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.variantDetails && (
            <small className="text-muted">{record.variantDetails}</small>
          )}
        </div>
      ),
    },
    {
      title: "Sale Price",
      dataIndex: "salePrice",
      render: (text) => (
        <span>${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      render: (text) => (
        <span>${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      render: (text) => (
        <span>{text}</span>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (text) => (
        <span className="fw-bold">${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
  ];

  // --- Columns for new items ---
  const newItemsColumns = [
    {
      title: "Item Name",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.variantDetails && (
            <small className="text-muted">{record.variantDetails}</small>
          )}
        </div>
      ),
    },
    {
      title: "Sale Price",
      dataIndex: "salePrice",
      render: (text) => (
        <span>${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      render: (text) => (
        <span>${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      render: (text) => (
        <span>{text}</span>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (text) => (
        <span className="fw-bold">${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header d-flex align-items-center justify-content-between">
          <div className="page-title">
            <h4>View Exchange Receipt</h4>
            <small className="text-success d-block">
              <i className="fas fa-exchange-alt me-1"></i>
              Exchange Receipt
            </small>
          </div>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-outline-primary me-2"
              onClick={() => navigate('/sales')}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Back to Sales
            </button>
            <button disabled={loading} className="btn btn-added" onClick={() => handleGeneratePdf()}>
              <PlusCircle className="me-2 iconsize" />
              Print Receipt
            </button>
          </div>
        </div>

        <div className="card-body mb-3">
          <div id='receiptToPrint' className="card m-auto" style={{ width: '950px' }}>
            <div ref={invoiceRef}>
              <div className="card-body add-product p-4">
                <div className="row justify-content-between mb-1">
                  <div className="col-md-4">
                    <img src={storeLogo} alt="img" style={{ height: 'auto', width: 'auto', objectFit: 'contain' }} />
                    <p className="mb-1">{userData?.store?.address.split(',')[0]}</p>
                    <p className="mb-1">{userData?.store?.address.split(', ').splice(1).join(', ')}</p>
                    <p className="mb-1">{userData?.store?.phone_number}</p>
                  </div>
                  <div className="col-md-2">
                    <img src={storeQrCode} alt="img" style={{ height: 'auto', width: 'auto', objectFit: 'contain' }} />
                  </div>
                  <div className="col-md-5">
                    <h3 className="mb-1">EXCHANGE RECEIPT</h3>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Receipt ID:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.receiptId}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Original Invoice:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.originalInvoice?.orderNumber}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Exchange Date:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{dayjs(receiptData?.exchangeDate).format('MM-DD-YYYY')}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Status:</p>
                      </div>
                      <div className="col-md-8">
                        <span className={`badge ${receiptData?.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                          {receiptData?.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row justify-content-between">
                  <div className="col-md-5">
                    <h6 className="text-center py-1" style={{ backgroundColor: 'rgb(40, 167, 69)', color: '#fff' }}>Customer Info</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Name:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.name}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Phone:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.phone}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Email:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <h6 className="text-center py-1" style={{ backgroundColor: 'rgb(40, 167, 69)', color: '#fff' }}>Delivery Info</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Address:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Original Items Section */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#dc3545', borderBottom: '2px solid #dc3545', paddingBottom: '5px' }}>
                    <i className="fas fa-arrow-up me-2"></i>
                    Original Items Returned
                  </h5>
                  <Table
                    className="table datanew dataTable no-footer custom-table"
                    columns={originalItemsColumns}
                    dataSource={receiptData?.originalItems || []}
                    pagination={false}
                    size="small"
                  />
                </div>

                {/* New Items Section */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#28a745', borderBottom: '2px solid #28a745', paddingBottom: '5px' }}>
                    <i className="fas fa-arrow-down me-2"></i>
                    New Items Purchased
                  </h5>
                  <Table
                    className="table datanew dataTable no-footer custom-table"
                    columns={newItemsColumns}
                    dataSource={receiptData?.newItems || []}
                    pagination={false}
                    size="small"
                  />
                </div>

                {/* Exchange Summary Section */}
                <div className="row">
                  {/* Items Summary - Left Side */}
                  <div className="col-md-6">
                    <div className="mb-4">
                      <h5 className="mb-3" style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>
                        <i className="fas fa-calculator me-2"></i>
                        Items Summary
                      </h5>
                      <div className="card" style={{ border: '1px solid #e9ecef' }}>
                        <div className="card-body p-3">
                          <div className="mb-3">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Original Items Value:</span>
                              <span className="fw-bold text-danger">
                                ${parseFloat(receiptData?.summary?.originalItemsTotal || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">New Items Value:</span>
                              <span className="fw-bold text-success">
                                ${parseFloat(receiptData?.summary?.newItemsTotal || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="border-top pt-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Store Credit Applied:</span>
                              <span className="fw-bold text-success">
                                -${parseFloat(receiptData?.summary?.storeCredit || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Information */}
                    {receiptData?.summary?.payments && receiptData.summary.payments.length > 0 && (
                      <div className="mb-4">
                        <h5 className="mb-3" style={{ color: '#28a745', borderBottom: '2px solid #28a745', paddingBottom: '5px' }}>
                          <i className="fas fa-credit-card me-2"></i>
                          Payment Information
                        </h5>
                        <div className="card" style={{ border: '1px solid #e9ecef' }}>
                          <div className="card-body p-3">
                            {receiptData.summary.payments.map((payment, index) => (
                              <div key={index} className="mb-2 pb-2 border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <span className="fw-bold">{payment.paymentMethod}</span>
                                    {payment.lastFourDigits && (
                                      <small className="text-muted ms-2">
                                        ****{payment.lastFourDigits}
                                      </small>
                                    )}
                                  </div>
                                  <span className="fw-bold text-success">
                                    ${payment.amount}
                                  </span>
                                </div>
                                <small className="text-muted">
                                  {dayjs(payment.paymentDate).format('MM/DD/YYYY HH:mm')}
                                </small>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Financial Breakdown - Right Side */}
                  <div className="col-md-6">
                    <div className="mb-4">
                      <h5 className="mb-3" style={{ color: '#dc3545', borderBottom: '2px solid #dc3545', paddingBottom: '5px' }}>
                        <i className="fas fa-money-bill-wave me-2"></i>
                        Financial Breakdown
                      </h5>
                      <div className="card" style={{ border: '1px solid #dc3545' }}>
                        <div className="card-body p-3">
                          <div className="mb-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Exchange Fees (10%):</span>
                              <span className="fw-bold text-danger">
                                -${parseFloat(receiptData?.summary?.exchangeFees || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="mb-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Pickup Charges:</span>
                              <span className="fw-bold text-danger">
                                -${parseFloat(receiptData?.summary?.pickupCharges || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="mb-3 border-bottom pb-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Amount Due Before Tax:</span>
                              <span className="fw-bold">
                                ${parseFloat(receiptData?.summary?.amountDueBeforeTax || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="mb-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Tax Amount ({receiptData?.summary?.taxRate}%):</span>
                              <span className="fw-bold">
                                ${parseFloat(receiptData?.summary?.taxAmount || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="border-top pt-2">
                            <div className="d-flex justify-content-between">
                              <span className="fw-bold text-primary">Total Amount Due:</span>
                              <span className="fs-5 fw-bold text-primary">
                                ${parseFloat(receiptData?.summary?.totalDue || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-lg-12">
                    <div className="total-order w-100 max-widthauto m-auto text-center">
                      <p className="mb-2">Thank you for your business. Exchange has been processed successfully.</p>
                      <small className="text-muted">This receipt serves as proof of exchange transaction.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeReceipt;